// Device controls ported from today's web/src/lib/push.ts.
import { ApiError } from '../api/client'
import type { BoardStore } from '../store/store'
type PushApi = ReturnType<BoardStore['notifications']>
import type { PushDevice, PushTestResponse } from '../api/types'

/** The service worker: at the root, so that its scope is the whole board. */
export const WORKER_URL = '/sw.js'

/** Where this browser remembers the device the hub holds for its subscription. */
export const DEVICE_KEY = 'poulpe.push.device'

/**
 * Whether this browser can show a push: it takes a service worker, the Push
 * API and notifications. An iPhone's Safari has them only for a board kept
 * on the Home Screen.
 */
export function canPush(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

/**
 * What this device calls itself, from its user agent: a phone or a tablet by
 * its own name ("iPhone"), a computer by its system and its browser
 * ("Mac · Safari").
 */
export function deviceLabel(userAgent = navigator.userAgent, touchPoints = navigator.maxTouchPoints ?? 0): string {
  if (/iPhone|iPod/.test(userAgent)) return 'iPhone'
  // An iPad asks for the desktop site: it says it is a Mac, one with a touch screen.
  if (/iPad/.test(userAgent) || (/Macintosh/.test(userAgent) && touchPoints > 1)) return 'iPad'
  const system = /Android/.test(userAgent)
    ? 'Android'
    : /Macintosh|Mac OS X/.test(userAgent)
      ? 'Mac'
      : /Windows/.test(userAgent)
        ? 'Windows'
        : /CrOS/.test(userAgent)
          ? 'ChromeOS'
          : /Linux/.test(userAgent)
            ? 'Linux'
            : null
  // Each of these names the ones after it too: the first that matches is the browser.
  const browser = /Edg\w*\//.test(userAgent)
    ? 'Edge'
    : /OPR\//.test(userAgent)
      ? 'Opera'
      : /Firefox\//.test(userAgent)
        ? 'Firefox'
        : /Chrom(?:e|ium)\//.test(userAgent)
          ? 'Chrome'
          : /Safari\//.test(userAgent)
            ? 'Safari'
            : null
  return [system, browser].filter(Boolean).join(' · ') || 'This device'
}

/** The hub's Web Push key, base64url, as the bytes `pushManager.subscribe` takes. */
export function keyBytes(key: string): Uint8Array<ArrayBuffer> {
  const base64 = key.replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4))
  const bytes = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i)
  return bytes
}

/** A device from the API; null when it is not one. */
export function toDevice(value: unknown): PushDevice | null {
  if (typeof value !== 'object' || value === null) return null
  const device = value as Record<string, unknown>
  if (typeof device.id !== 'string' || !device.id) return null
  return {
    id: device.id,
    label: typeof device.label === 'string' && device.label ? device.label : null,
    created_at: typeof device.created_at === 'string' ? device.created_at : '',
    last_ok_at: typeof device.last_ok_at === 'string' ? device.last_ok_at : null,
    failures: typeof device.failures === 'number' ? device.failures : 0,
  }
}

interface Remembered {
  id: string
  endpoint: string
}

function remembered(identity: string): Remembered | null {
  try {
    const held = JSON.parse(window.localStorage.getItem(`${DEVICE_KEY}:${identity}`) ?? 'null') as Partial<Remembered> | null
    return held && typeof held.id === 'string' && typeof held.endpoint === 'string' ? { id: held.id, endpoint: held.endpoint } : null
  } catch {
    return null
  }
}

function remember(identity: string, device: Remembered | null): void {
  try {
    if (device) window.localStorage.setItem(`${DEVICE_KEY}:${identity}`, JSON.stringify(device))
    else window.localStorage.removeItem(`${DEVICE_KEY}:${identity}`)
  } catch {
    // A private window keeps nothing: after a reload the device reads as off, and turning it on finds it again.
  }
}

/**
 * Register the service worker and wait for it to be active: a push
 * subscription belongs to an active worker. Called only where the server
 * pushes (`GET /api/push` says `supported`).
 */
export async function pushRegistration(): Promise<ServiceWorkerRegistration> {
  await navigator.serviceWorker.register(WORKER_URL)
  return navigator.serviceWorker.ready
}

/**
 * The device the hub holds for this browser's subscription, while the two
 * agree: the browser may notify and is subscribed, and the hub lists the
 * device it registered for that subscription (`devices`, from `GET
 * /api/push`). Anything else reads as off, and nothing is written: turning
 * notifications on again puts it right, with a subscription of its own.
 */
export async function currentDevice(registration: ServiceWorkerRegistration, devices: readonly PushDevice[], identity: string): Promise<PushDevice | null> {
  if (Notification.permission !== 'granted') return null
  const subscription = await registration.pushManager.getSubscription()
  const held = remembered(identity)
  if (!subscription || !held || held.endpoint !== subscription.endpoint) return null
  return devices.find((device) => device.id === held.id) ?? null
}

/**
 * A new subscription, made with the hub's key. One the browser still holds
 * is given up first: the hub does not hold it (it dropped the device when
 * the push service said it was gone, or its own key was made anew), so it is
 * not taken up again.
 */
async function subscribe(registration: ServiceWorkerRegistration, key: string): Promise<PushSubscription> {
  await (await registration.pushManager.getSubscription())?.unsubscribe()
  return registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(key) })
}

/**
 * Turn notifications on. The browser asks (call this straight from a click:
 * a browser asks only then), subscribes with the hub's key, and the hub
 * registers the subscription under this device's name. Null when the
 * browser's answer is no.
 */
export async function turnOn(api: PushApi, identity: string, registration: ServiceWorkerRegistration, key: string): Promise<PushDevice | null> {
  await Notification.requestPermission()
  if (Notification.permission !== 'granted') return null
  const subscription = await subscribe(registration, key)
  const { endpoint, keys } = subscription.toJSON()
  if (!endpoint || !keys?.p256dh || !keys.auth) throw new Error('The browser gave a push subscription without its keys.')
  const answer = await api.registerPushDevice({ endpoint, keys: { p256dh: keys.p256dh, auth: keys.auth }, label: deviceLabel() })
  // `{device}`; an answer that is the device itself is read too.
  const device = toDevice(answer?.device) ?? toDevice(answer)
  if (!device) throw new Error('The board did not say which device it registered.')
  remember(identity, { id: device.id, endpoint })
  return device
}

/**
 * Turn them off: the hub forgets the device, then the browser gives its
 * subscription up. A device the hub holds no more is as good as removed.
 */
export async function turnOff(api: PushApi, identity: string, registration: ServiceWorkerRegistration, device: PushDevice): Promise<void> {
  try {
    await api.removePushDevice(device.id)
  } catch (err) {
    if (!(err instanceof ApiError && err.status === 404)) throw err
  }
  remember(identity, null)
  try {
    await (await registration.pushManager.getSubscription())?.unsubscribe()
  } catch {
    // The hub pushes to it no more, which is what was asked; the browser's end of it goes with its worker.
  }
}

/**
 * What a test reached, in words. A result names its device by its id, or
 * carries the device itself; one the list does not hold is "a device".
 */
export function describeTest(response: PushTestResponse | undefined, devices: readonly PushDevice[]): { text: string; failed: boolean } {
  const sent: string[] = []
  const missed: string[] = []
  for (const result of Array.isArray(response?.results) ? response.results : []) {
    const device = typeof result?.device === 'string' ? devices.find((d) => d.id === result.device) : toDevice(result?.device)
    const label = device?.label ?? 'a device'
    if (result?.ok) sent.push(label)
    else missed.push(`The test did not reach ${label}${typeof result?.error === 'string' && result.error ? `: ${result.error}` : ''}.`)
  }
  if (sent.length === 0 && missed.length === 0) return { text: 'The board holds no device to send a test to.', failed: true }
  return { text: [sent.length ? `Test sent to ${sent.join(', ')}.` : '', ...missed].filter(Boolean).join(' '), failed: missed.length > 0 }
}

